import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
    FolderKanban,
    Plus,
    Search,
    Filter,
    Loader2,
    AlertCircle,
    FileText,
    Trash2,
    Edit,
    Download,
    Calendar,
    Users,
    HardDrive,
    FileSpreadsheet,
    FileCode,
    FileArchive,
    CheckCircle2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface ProjectItem {
    id: number;
    title: string;
    description: string | null;
    file_path: string;
    original_file_name: string;
    mime_type: string | null;
    file_size: number | null;
    file_extension: string;
    formatted_file_size: string | null;
    cohort_id: string;
    storage_provider: string;
    created_at: string;
    updated_at: string;
    cohort?: {
        id: string;
        name: string;
    };
    creator?: {
        id: number;
        name: string;
        email: string;
    };
}

export default function InstructorProjects() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState<ProjectItem[]>([]);
    const [cohorts, setCohorts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCohort, setSelectedCohort] = useState('all');
    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const fetchCohorts = async () => {
        try {
            const res = await fetch(`${API_URL}/cohorts`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                setCohorts(data);
            }
        } catch (err) {
            console.error('Failed to load cohorts:', err);
        }
    };

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/instructor/projects`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.status === 401) {
                logout();
                navigate('/instructor-login');
                return;
            }

            const data = await response.json();
            if (response.ok) {
                setProjects(data);
                setError(null);
            } else {
                throw new Error(data.message || 'Failed to fetch projects.');
            }
        } catch (err: any) {
            console.error('Fetch Projects Error:', err);
            if (err.message === 'Failed to fetch' || err.message.includes('NetworkError')) {
                toast.error('Connection failed. Redirecting to login...');
                setTimeout(() => {
                    logout();
                    navigate('/instructor-login');
                }, 2000);
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCohorts();
        fetchProjects();
    }, []);

    const handleDelete = async (id: number, title: string) => {
        if (!window.confirm(`Are you sure you want to delete the project "${title}"?`)) {
            return;
        }

        try {
            setDeletingId(id);
            const response = await fetch(`${API_URL}/instructor/projects/${id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                setProjects(prev => prev.filter(p => p.id !== id));
                toast.success('Project deleted successfully.');
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to delete project.');
            }
        } catch (err: any) {
            toast.error(err.message || 'A network error occurred while deleting.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleDownload = async (project: ProjectItem) => {
        try {
            setDownloadingId(project.id);
            toast.loading(`Downloading ${project.original_file_name || project.title}...`, { id: 'project-dl' });
            
            const response = await fetch(`${API_URL}/projects/${project.id}/download`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Download failed or file not accessible.');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = project.original_file_name || `${project.title}.${project.file_extension || 'bin'}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            toast.success('Download completed!', { id: 'project-dl' });
        } catch (err: any) {
            toast.error(err.message || 'Unable to download file.', { id: 'project-dl' });
        } finally {
            setDownloadingId(null);
        }
    };

    const getFileBadge = (ext: string) => {
        const lower = (ext || '').toLowerCase();
        if (['xls', 'xlsx', 'csv'].includes(lower)) {
            return {
                icon: FileSpreadsheet,
                bg: 'rgba(16, 185, 129, 0.1)',
                color: '#059669',
                label: lower.toUpperCase() || 'EXCEL'
            };
        }
        if (lower === 'pdf') {
            return {
                icon: FileText,
                bg: 'rgba(239, 68, 68, 0.1)',
                color: '#dc2626',
                label: 'PDF'
            };
        }
        if (['doc', 'docx'].includes(lower)) {
            return {
                icon: FileText,
                bg: 'rgba(59, 130, 246, 0.1)',
                color: '#2563eb',
                label: lower.toUpperCase()
            };
        }
        if (['ppt', 'pptx'].includes(lower)) {
            return {
                icon: FileText,
                bg: 'rgba(249, 115, 22, 0.1)',
                color: '#ea580c',
                label: lower.toUpperCase()
            };
        }
        if (['zip', 'rar', '7z'].includes(lower)) {
            return {
                icon: FileArchive,
                bg: 'rgba(139, 92, 246, 0.1)',
                color: '#7c3aed',
                label: 'ARCHIVE'
            };
        }
        return {
            icon: FileCode,
            bg: 'rgba(100, 116, 139, 0.1)',
            color: '#475569',
            label: lower.toUpperCase() || 'FILE'
        };
    };

    const filteredProjects = projects.filter(p => {
        const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.original_file_name && p.original_file_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.cohort?.name && p.cohort.name.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesCohort = selectedCohort === 'all' || p.cohort_id === selectedCohort;

        return matchesSearch && matchesCohort;
    });

    const uniqueCohortsCount = new Set(projects.map(p => p.cohort_id)).size;
    const totalBytes = projects.reduce((acc, curr) => acc + (curr.file_size || 0), 0);
    const formattedTotalStorage = totalBytes > 0
        ? (totalBytes / (1024 * 1024)).toFixed(1) + ' MB'
        : '0 MB';

    return (
        <div className="projects-page-container">
            <style>{`
                .projects-page-container,
                .staff-scope .projects-page-container {
                    max-width: 1280px;
                    margin: 0 auto;
                    font-family: 'Inter', sans-serif;
                    padding: 1rem 1.25rem 3rem 1.25rem;
                    box-sizing: border-box;
                    width: 100%;
                }

                .page-header-box,
                .staff-scope .page-header-box {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.75rem;
                    flex-wrap: wrap;
                    gap: 1.25rem;
                }

                .header-left,
                .staff-scope .header-left {
                    flex: 1;
                    min-width: 260px;
                }

                .header-left h1,
                .staff-scope .header-left h1 {
                    font-size: 2rem;
                    font-weight: 900;
                    color: var(--index-text-heading);
                    margin: 0 0 0.35rem 0;
                    letter-spacing: -0.03em;
                    display: flex;
                    align-items: center;
                    gap: 0.65rem;
                    flex-wrap: wrap;
                }

                .header-left p,
                .staff-scope .header-left p {
                    color: var(--index-text-secondary);
                    font-weight: 600;
                    margin: 0;
                    font-size: 0.95rem;
                    line-height: 1.45;
                }

                .btn-primary-action,
                .staff-scope .btn-primary-action {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: var(--index-primary-color);
                    color: white;
                    padding: 0.75rem 1.5rem;
                    min-height: 44px;
                    border-radius: 14px;
                    text-decoration: none;
                    font-weight: 800;
                    font-size: 0.92rem;
                    transition: all 0.25s ease;
                    box-shadow: 0 8px 16px -3px color-mix(in srgb, var(--index-primary-color) 30%, transparent);
                    border: none;
                    cursor: pointer;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .btn-primary-action:hover,
                .staff-scope .btn-primary-action:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 20px -3px color-mix(in srgb, var(--index-primary-color) 40%, transparent);
                    color: white;
                }

                .stats-grid,
                .staff-scope .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                    gap: 1rem;
                    margin-bottom: 1.75rem;
                }

                .stat-card,
                .staff-scope .stat-card {
                    background: var(--index-card-bg);
                    border: 1px solid var(--index-border-color);
                    border-radius: 18px;
                    padding: 1.15rem 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    min-width: 0;
                }

                .stat-icon-wrap,
                .staff-scope .stat-icon-wrap {
                    width: 46px;
                    height: 46px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .stat-meta,
                .staff-scope .stat-meta {
                    min-width: 0;
                    flex: 1;
                }

                .stat-meta h4,
                .staff-scope .stat-meta h4 {
                    margin: 0;
                    font-size: 1.6rem;
                    font-weight: 900;
                    color: var(--index-text-heading);
                    line-height: 1.1;
                }

                .stat-meta p,
                .staff-scope .stat-meta p {
                    margin: 0.2rem 0 0 0;
                    font-size: 0.82rem;
                    font-weight: 600;
                    color: var(--index-text-secondary);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .filters-bar,
                .staff-scope .filters-bar {
                    background: var(--index-card-bg);
                    border: 1px solid var(--index-border-color);
                    border-radius: 16px;
                    padding: 0.85rem 1rem;
                    margin-bottom: 1.75rem;
                    display: flex;
                    gap: 0.85rem;
                    align-items: center;
                    flex-wrap: wrap;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }

                .search-box-wrap,
                .staff-scope .search-box-wrap {
                    flex: 1;
                    min-width: 220px;
                    position: relative;
                }

                .search-box-icon,
                .staff-scope .search-box-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: var(--index-text-faint);
                    pointer-events: none;
                }

                .search-input-field,
                .staff-scope .search-input-field {
                    width: 100%;
                    height: 44px;
                    padding: 0.65rem 1rem 0.65rem 2.5rem;
                    border: 1px solid var(--index-border-color);
                    border-radius: 12px;
                    font-size: 0.92rem;
                    font-weight: 600;
                    background: var(--index-hover-bg);
                    color: var(--index-text-heading);
                    outline: none;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }

                .search-input-field:focus,
                .staff-scope .search-input-field:focus {
                    border-color: var(--index-primary-color);
                    background: var(--index-card-bg);
                }

                .cohort-select-wrap,
                .staff-scope .cohort-select-wrap {
                    min-width: 200px;
                }

                .cohort-select-dropdown,
                .staff-scope .cohort-select-dropdown {
                    width: 100%;
                    height: 44px;
                    padding: 0.65rem 1rem;
                    border: 1px solid var(--index-border-color);
                    border-radius: 12px;
                    font-size: 0.92rem;
                    font-weight: 600;
                    background: var(--index-hover-bg);
                    color: var(--index-text-heading);
                    outline: none;
                    cursor: pointer;
                    box-sizing: border-box;
                }

                .cohort-select-dropdown:focus,
                .staff-scope .cohort-select-dropdown:focus {
                    border-color: var(--index-primary-color);
                    background: var(--index-card-bg);
                }

                .projects-grid,
                .staff-scope .projects-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
                    gap: 1.25rem;
                    width: 100%;
                }

                .project-card,
                .staff-scope .project-card {
                    background: var(--index-card-bg);
                    border: 1px solid var(--index-border-color);
                    border-radius: 20px;
                    padding: 1.35rem;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
                    min-width: 0;
                    box-sizing: border-box;
                }

                .project-card:hover,
                .staff-scope .project-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 24px -6px rgba(0, 0, 0, 0.08);
                    border-color: color-mix(in srgb, var(--index-primary-color) 40%, var(--index-border-color));
                }

                .project-card-header,
                .staff-scope .project-card-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.85rem;
                    margin-bottom: 0.85rem;
                }

                .format-icon-box,
                .staff-scope .format-icon-box {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .project-title-area,
                .staff-scope .project-title-area {
                    flex: 1;
                    min-width: 0;
                }

                .project-title,
                .staff-scope .project-title {
                    font-size: 1.1rem;
                    font-weight: 800;
                    color: var(--index-text-heading);
                    margin: 0 0 0.35rem 0;
                    line-height: 1.3;
                    word-break: break-word;
                }

                .cohort-badge,
                .staff-scope .cohort-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 0.2rem 0.6rem;
                    background: rgba(16, 185, 129, 0.08);
                    color: var(--index-primary-color);
                    border-radius: 8px;
                    font-size: 0.75rem;
                    font-weight: 700;
                    max-width: 100%;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    box-sizing: border-box;
                }

                .project-desc,
                .staff-scope .project-desc {
                    color: var(--index-text-secondary);
                    font-size: 0.88rem;
                    line-height: 1.45;
                    margin: 0 0 1rem 0;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    font-weight: 500;
                }

                .project-file-meta-bar,
                .staff-scope .project-file-meta-bar {
                    background: var(--index-hover-bg);
                    border-radius: 12px;
                    padding: 0.65rem 0.85rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: var(--index-text-secondary);
                    margin-bottom: 1.15rem;
                    gap: 0.5rem;
                    min-width: 0;
                }

                .file-name-truncated,
                .staff-scope .file-name-truncated {
                    flex: 1;
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: var(--index-text-heading);
                    font-weight: 700;
                }

                .file-size-badge,
                .staff-scope .file-size-badge {
                    flex-shrink: 0;
                    white-space: nowrap;
                    font-weight: 700;
                }

                .project-card-footer,
                .staff-scope .project-card-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-top: 1px solid var(--index-border-color);
                    padding-top: 0.95rem;
                    gap: 0.65rem;
                    flex-wrap: wrap;
                }

                .card-date-meta,
                .staff-scope .card-date-meta {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 0.78rem;
                    color: var(--index-text-faint);
                    font-weight: 600;
                    white-space: nowrap;
                }

                .action-btn-group,
                .staff-scope .action-btn-group {
                    display: flex;
                    align-items: center;
                    gap: 0.45rem;
                }

                .btn-icon-action,
                .staff-scope .btn-icon-action {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid var(--index-border-color);
                    background: var(--index-card-bg);
                    color: var(--index-text-secondary);
                    cursor: pointer;
                    transition: all 0.2s;
                    text-decoration: none;
                    flex-shrink: 0;
                }

                .btn-icon-action:hover,
                .staff-scope .btn-icon-action:hover {
                    background: var(--index-hover-bg);
                    color: var(--index-text-heading);
                    border-color: var(--index-primary-color);
                }

                .btn-icon-action.btn-delete:hover,
                .staff-scope .btn-icon-action.btn-delete:hover {
                    background: rgba(239, 68, 68, 0.08);
                    color: #dc2626;
                    border-color: #ef4444;
                }

                .btn-download-pill,
                .staff-scope .btn-download-pill {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 0.55rem 0.95rem;
                    min-height: 40px;
                    background: var(--index-primary-color);
                    color: white;
                    border-radius: 10px;
                    font-size: 0.85rem;
                    font-weight: 800;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }

                .btn-download-pill:hover,
                .staff-scope .btn-download-pill:hover {
                    filter: brightness(1.1);
                    transform: translateY(-1px);
                    color: white;
                }

                /* Mobile & Tablet Responsive Media Queries */
                @media (max-width: 768px) {
                    .projects-page-container,
                    .staff-scope .projects-page-container {
                        padding: 0.75rem 0.75rem 2.5rem 0.75rem;
                    }

                    .page-header-box,
                    .staff-scope .page-header-box {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 1rem;
                        margin-bottom: 1.25rem;
                    }

                    .header-left h1,
                    .staff-scope .header-left h1 {
                        font-size: 1.45rem !important;
                        gap: 0.5rem;
                    }

                    .header-left p,
                    .staff-scope .header-left p {
                        font-size: 0.85rem !important;
                    }

                    .btn-primary-action,
                    .staff-scope .btn-primary-action {
                        width: 100%;
                        justify-content: center;
                        padding: 0.85rem;
                    }

                    .stats-grid,
                    .staff-scope .stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 0.65rem;
                        margin-bottom: 1.25rem;
                    }

                    .stat-card,
                    .staff-scope .stat-card {
                        padding: 0.85rem;
                        gap: 0.65rem;
                        border-radius: 16px;
                    }

                    .stat-icon-wrap,
                    .staff-scope .stat-icon-wrap {
                        width: 38px;
                        height: 38px;
                        border-radius: 10px;
                    }

                    .stat-meta h4,
                    .staff-scope .stat-meta h4 {
                        font-size: 1.25rem;
                    }

                    .stat-meta p,
                    .staff-scope .stat-meta p {
                        font-size: 0.72rem;
                    }

                    .filters-bar,
                    .staff-scope .filters-bar {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 0.65rem;
                        padding: 0.75rem;
                        border-radius: 16px;
                        margin-bottom: 1.25rem;
                    }

                    .search-box-wrap,
                    .staff-scope .search-box-wrap {
                        width: 100%;
                        min-width: 0;
                    }

                    .search-input-field,
                    .staff-scope .search-input-field {
                        font-size: 1rem; /* Prevents iOS Safari auto-zoom */
                    }

                    .cohort-select-wrap,
                    .staff-scope .cohort-select-wrap {
                        width: 100%;
                        min-width: 0;
                    }

                    .cohort-select-dropdown,
                    .staff-scope .cohort-select-dropdown {
                        font-size: 1rem; /* Prevents iOS Safari auto-zoom */
                    }

                    .projects-grid,
                    .staff-scope .projects-grid {
                        grid-template-columns: 1fr;
                        gap: 1rem;
                    }

                    .project-card,
                    .staff-scope .project-card {
                        padding: 1.15rem;
                        border-radius: 18px;
                    }

                    .format-icon-box,
                    .staff-scope .format-icon-box {
                        width: 44px;
                        height: 44px;
                    }

                    .project-title,
                    .staff-scope .project-title {
                        font-size: 1.05rem;
                    }

                    .project-card-footer,
                    .staff-scope .project-card-footer {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 0.75rem;
                    }

                    .card-date-meta,
                    .staff-scope .card-date-meta {
                        width: 100%;
                        justify-content: flex-start;
                    }

                    .action-btn-group,
                    .staff-scope .action-btn-group {
                        width: 100%;
                        justify-content: space-between;
                        gap: 0.5rem;
                    }

                    .btn-download-pill,
                    .staff-scope .btn-download-pill {
                        flex: 1;
                    }
                }

                @media (max-width: 380px) {
                    .stats-grid,
                    .staff-scope .stats-grid {
                        grid-template-columns: 1fr;
                    }

                    .stat-card,
                    .staff-scope .stat-card {
                        padding: 0.75rem 0.85rem;
                    }
                }
            `}</style>

            {/* Header */}
            <div className="page-header-box">
                <div className="header-left">
                    <h1>
                        <FolderKanban className="text-brand-emerald" size={28} />
                        Project Management
                    </h1>
                    <p>Upload, configure, and manage downloadable cohort project files and workbooks.</p>
                </div>
                <Link to="/instructor/projects/create" className="btn-primary-action">
                    <Plus size={18} strokeWidth={3} />
                    <span>Create Project</span>
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669' }}>
                        <FolderKanban size={22} />
                    </div>
                    <div className="stat-meta">
                        <h4>{projects.length}</h4>
                        <p>Total Projects</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon-wrap" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#2563eb' }}>
                        <Users size={22} />
                    </div>
                    <div className="stat-meta">
                        <h4>{uniqueCohortsCount}</h4>
                        <p>Cohorts Assigned</p>
                    </div>
                </div>
            </div>

            {/* Search & Cohort Filter */}
            <div className="filters-bar">
                <div className="search-box-wrap">
                    <Search className="search-box-icon" size={18} />
                    <input
                        type="text"
                        className="search-input-field"
                        placeholder="Search projects by title, filename, cohort..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="cohort-select-wrap">
                    <select
                        className="cohort-select-dropdown"
                        value={selectedCohort}
                        onChange={(e) => setSelectedCohort(e.target.value)}
                    >
                        <option value="all">All Cohorts ({projects.length})</option>
                        {cohorts.map((c: any) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Content Area */}
            {loading ? (
                <div className="flex flex-col items-center justify-center p-8 sm:p-14 bg-white dark:bg-brand-charcoal border border-gray-100 dark:border-gray-800 rounded-3xl text-center">
                    <Loader2 className="animate-spin text-brand-emerald mb-3" size={36} />
                    <p className="font-bold text-gray-500 dark:text-gray-400 text-sm">Loading project repository...</p>
                </div>
            ) : error ? (
                <div className="bg-red-50 dark:bg-red-950/30 p-6 sm:p-8 rounded-2xl border border-red-200 dark:border-red-900/40 text-center space-y-3">
                    <AlertCircle className="mx-auto text-red-500" size={32} />
                    <p className="font-bold text-red-800 dark:text-red-300 text-sm">{error}</p>
                    <button
                        onClick={fetchProjects}
                        className="px-5 py-2.5 bg-red-600 text-white rounded-xl font-bold text-sm hover:bg-red-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            ) : filteredProjects.length === 0 ? (
                <div className="bg-white dark:bg-brand-charcoal border border-gray-100 dark:border-gray-800 p-8 sm:p-12 md:p-16 rounded-3xl text-center space-y-4 shadow-xs">
                    <div className="w-14 h-14 bg-emerald-50 dark:bg-emerald-950/40 rounded-2xl flex items-center justify-center mx-auto text-brand-emerald">
                        <FolderKanban size={28} />
                    </div>
                    <div>
                        <h3 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white mb-1">No Projects Found</h3>
                        <p className="text-gray-500 dark:text-gray-400 font-medium max-w-md mx-auto text-xs sm:text-sm">
                            {searchTerm || selectedCohort !== 'all'
                                ? 'No projects match your filter criteria. Try adjusting your search query.'
                                : 'Get started by creating your first cohort project workbook or exercise file.'}
                        </p>
                    </div>
                    <Link to="/instructor/projects/create" className="btn-primary-action inline-flex">
                        <Plus size={18} strokeWidth={3} />
                        <span>Upload Project File</span>
                    </Link>
                </div>
            ) : (
                <div className="projects-grid">
                    {filteredProjects.map((project) => {
                        const badge = getFileBadge(project.file_extension);
                        const BadgeIcon = badge.icon;
                        const formattedDate = project.created_at
                            ? new Date(project.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                            })
                            : 'Recent';

                        return (
                            <div key={project.id} className="project-card">
                                <div>
                                    <div className="project-card-header">
                                        <div
                                            className="format-icon-box"
                                            style={{ background: badge.bg, color: badge.color }}
                                            title={badge.label}
                                        >
                                            <BadgeIcon size={24} />
                                        </div>
                                        <div className="project-title-area">
                                            <h3 className="project-title" title={project.title}>
                                                {project.title}
                                            </h3>
                                            <div className="cohort-badge" title={project.cohort?.name || 'Cohort'}>
                                                <Users size={11} />
                                                <span>{project.cohort?.name || 'Cohort ' + project.cohort_id}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {project.description && (
                                        <p className="project-desc">{project.description}</p>
                                    )}

                                    <div className="project-file-meta-bar">
                                        <span className="file-name-truncated" title={project.original_file_name}>
                                            {project.original_file_name || 'Project Document'}
                                        </span>
                                        <span className="file-size-badge">{project.formatted_file_size || badge.label}</span>
                                    </div>
                                </div>

                                <div className="project-card-footer">
                                    <div className="card-date-meta">
                                        <Calendar size={13} />
                                        <span>{formattedDate}</span>
                                    </div>

                                    <div className="action-btn-group">
                                        <button
                                            type="button"
                                            onClick={() => handleDownload(project)}
                                            disabled={downloadingId === project.id}
                                            className="btn-download-pill"
                                            title="Download Project File"
                                        >
                                            {downloadingId === project.id ? (
                                                <Loader2 size={15} className="animate-spin" />
                                            ) : (
                                                <Download size={15} />
                                            )}
                                            <span>Download</span>
                                        </button>

                                        <Link
                                            to={`/instructor/projects/${project.id}/edit`}
                                            className="btn-icon-action"
                                            title="Edit Project & Replace File"
                                        >
                                            <Edit size={16} />
                                        </Link>

                                        <button
                                            type="button"
                                            onClick={() => handleDelete(project.id, project.title)}
                                            disabled={deletingId === project.id}
                                            className="btn-icon-action btn-delete"
                                            title="Delete Project"
                                        >
                                            {deletingId === project.id ? (
                                                <Loader2 size={16} className="animate-spin text-red-500" />
                                            ) : (
                                                <Trash2 size={16} />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
